import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../supabase/supabase";

export const ATTACHMENTS_BUCKET =
  import.meta.env.VITE_ATTACHMENTS_BUCKET || "attachments";

export type StoredAttachment = {
  src?: string | null;
  path?: string | null;
  title?: string | null;
  type?: string | null;
};

const PUBLIC_MARKER = `/object/public/${ATTACHMENTS_BUCKET}/`;
const SIGNED_MARKER = `/object/sign/${ATTACHMENTS_BUCKET}/`;

/**
 * Resolve the storage path for an attachment, regardless of whether `path`
 * was stored or only a public URL in `src` (older records).
 */
export const attachmentPath = (
  attachment?: StoredAttachment | null,
): string | null => {
  if (!attachment) return null;
  if (attachment.path) return attachment.path;
  const src = attachment.src ?? "";
  for (const marker of [PUBLIC_MARKER, SIGNED_MARKER]) {
    const i = src.indexOf(marker);
    if (i >= 0) {
      return decodeURIComponent(src.slice(i + marker.length).split("?")[0]);
    }
  }
  if (src && !/^(?:[a-z][a-z\d+.-]*:|\/|\.\/|\.\.\/)/i.test(src)) {
    return src;
  }
  return null;
};

/**
 * Mint a signed URL for a storage object. Returns null when signing fails or
 * when the attachment has no path (external/non-storage src falls back to src).
 */
export const getSignedAttachmentUrl = async (
  attachment: StoredAttachment,
): Promise<string | null> => {
  const path = attachmentPath(attachment);
  if (!path) return null;
  const { data, error } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .createSignedUrl(path, 3600);
  if (error) return null;
  return data.signedUrl;
};

/**
 * React hook returning a signed URL (1h, cached ~50min) for a stored
 * attachment. Falls back to `attachment.src` when there is no storage path.
 */
export const useAttachmentUrl = (
  attachment?: StoredAttachment | null,
): string | null => {
  const path = attachmentPath(attachment);
  const { data } = useQuery({
    queryKey: ["signed-url", ATTACHMENTS_BUCKET, path],
    queryFn: () => getSignedAttachmentUrl({ path }),
    enabled: !!path,
    staleTime: 50 * 60 * 1000,
    refetchInterval: 50 * 60 * 1000,
  });
  if (path) return data ?? null;
  return attachment?.src ?? null;
};
